import type { InitializeOptions } from '@capgo/capacitor-social-login';

import { getGoogleConfig, getMicrosoftConfig } from 'services/config/runtimeConfig';

/**
 * Configuration for native SSO via `@capgo/capacitor-social-login`.
 *
 * - Google uses native Google Sign-In (Android Credential Manager / iOS GoogleSignIn).
 *   It returns an OIDC `id_token` whose audience is the **web** client id, the backend verifies that id_token.
 * - Microsoft uses the generic `oauth2` provider (authorisation-code + PKCE) against Azure AD.
 *   It runs in an in-app WebView and returns the `id_token`.
 *   No custom URL scheme or OS redirect registration is required.
 */

export type NativeOAuthProvider = 'microsoft' | 'google';

export const AZURE_PROVIDER_ID = 'azure';

const MICROSOFT_REDIRECT_URL = 'msauth.co.atlasone.app://auth';

/**
 * One-time plugin init config. Registers the native Google provider and the Azure oauth2 provider.
 * Microsoft does NOT set `resourceUrl`, we only need the id_token, and a resource fetch would be wasted work.
 *
 * The OAuth ids are read from the runtime config (not `import.meta.env`) so a single native binary can use the instance's credentials.
 * This is called lazily on the first login, after a workspace has been selected, so the config is always populated by the time it runs.
 */
export const buildInitializeOptions = (): InitializeOptions => {
  const google = getGoogleConfig();
  const microsoft = getMicrosoftConfig();

  return {
    google: {
      webClientId: google.webClientId,
      iOSClientId: google.iosClientId,
      // Forces the iOS id_token audience to the web client id so the backend check is uniform.
      iOSServerClientId: google.webClientId,
      mode: 'online'
    },
    oauth2: {
      [AZURE_PROVIDER_ID]: {
        appId: microsoft.clientId,
        authorizationBaseUrl: `https://login.microsoftonline.com/common/oauth2/v2.0/authorize`,
        accessTokenEndpoint: `https://login.microsoftonline.com/common/oauth2/v2.0/token`,
        redirectUrl: MICROSOFT_REDIRECT_URL,
        responseType: 'code',
        pkceEnabled: true,
        scope: 'openid profile email'
      }
    }
  };
};
